import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { jobsAPI, sourcingAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { toast } from 'sonner';

const NAVY = '#0076A3';

const CopyButton = ({ text, testId }) => (
  <button
    data-testid={testId}
    onClick={() => { navigator.clipboard.writeText(text || ''); toast.success('Copied to clipboard'); }}
    className="text-xs font-semibold px-2 py-1 rounded-md border border-slate-300 hover:bg-slate-100 transition-colors"
    style={{ color: NAVY }}
  >
    Copy
  </button>
);

const Section = ({ title, children, right }) => (
  <div className="mb-6">
    <div className="flex items-center justify-between mb-2">
      <h3 className="text-sm font-bold uppercase tracking-wide" style={{ color: NAVY }}>{title}</h3>
      {right}
    </div>
    {children}
  </div>
);

const BoolRow = ({ label, value, testId }) => value ? (
  <div className="mb-3">
    <div className="flex items-center justify-between mb-1">
      <span className="text-xs font-semibold text-slate-500">{label}</span>
      <CopyButton text={value} testId={`copy-${testId}`} />
    </div>
    <code data-testid={testId} className="block text-xs bg-slate-50 border border-slate-200 rounded-md p-3 break-words font-mono text-slate-700">{value}</code>
  </div>
) : null;

export default function SourcingPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [jobs, setJobs] = useState([]);
  const [jobId, setJobId] = useState(searchParams.get('job') || '');
  const [kit, setKit] = useState(null);
  const [genLoading, setGenLoading] = useState(false);
  const [matches, setMatches] = useState(null);
  const [matchLoading, setMatchLoading] = useState(false);
  const [addingId, setAddingId] = useState(null);

  const kitPollRef = useRef(null);
  const internalPollRef = useRef(null);
  const clearKitPoll = useCallback(() => { if (kitPollRef.current) { clearInterval(kitPollRef.current); kitPollRef.current = null; } }, []);
  const clearInternalPoll = useCallback(() => { if (internalPollRef.current) { clearInterval(internalPollRef.current); internalPollRef.current = null; } }, []);
  useEffect(() => () => { clearKitPoll(); clearInternalPoll(); }, [clearKitPoll, clearInternalPoll]);

  const loadJobs = useCallback(async () => {
    try {
      const res = await jobsAPI.list();
      setJobs(res.data || []);
      if (!jobId && res.data && res.data.length) setJobId(res.data[0]._id);
    } catch (e) { toast.error('Failed to load jobs'); }
  }, [jobId]);

  useEffect(() => { loadJobs(); }, [loadJobs]);

  const startKitPoll = useCallback((id) => {
    clearKitPoll();
    kitPollRef.current = setInterval(async () => {
      try {
        const res = await sourcingAPI.getKit(id);
        if (res.data.status === 'ready' && res.data.kit) {
          clearKitPoll(); setKit(res.data.kit); setGenLoading(false);
          toast.success('Sourcing kit ready');
        } else if (res.data.status === 'failed') {
          clearKitPoll(); setGenLoading(false);
          toast.error(res.data.error || 'Failed to generate sourcing kit');
        }
      } catch (e) { if (process.env.NODE_ENV === 'development') console.debug('Sourcing poll retry:', e?.message); }
    }, 3000);
  }, [clearKitPoll]);

  const startInternalPoll = useCallback((id) => {
    clearInternalPoll();
    internalPollRef.current = setInterval(async () => {
      try {
        const res = await sourcingAPI.getInternal(id);
        if (res.data.status === 'ready') {
          clearInternalPoll(); setMatches(res.data); setMatchLoading(false);
          if (!res.data.matches?.length) toast.info(`No matches found (searched ${res.data.pool_size} candidates)`);
        } else if (res.data.status === 'failed') {
          clearInternalPoll(); setMatchLoading(false);
          toast.error(res.data.error || 'Failed to rank internal candidates');
        }
      } catch (e) { if (process.env.NODE_ENV === 'development') console.debug('Sourcing poll retry:', e?.message); }
    }, 3000);
  }, [clearInternalPoll]);

  const loadKit = useCallback(async (id) => {
    clearKitPoll(); clearInternalPoll();
    setKit(null); setMatches(null); setGenLoading(false); setMatchLoading(false);
    if (!id) return;
    try {
      const res = await sourcingAPI.getKit(id);
      if (res.data.status === 'ready' && res.data.kit) setKit(res.data.kit);
      else if (res.data.status === 'generating') { setGenLoading(true); startKitPoll(id); }
    } catch (e) {
      if (process.env.NODE_ENV === 'development') console.debug('No cached sourcing kit:', e?.message);
    }
    try {
      const r = await sourcingAPI.getInternal(id);
      if (r.data.status === 'ready' && (r.data.matches?.length || r.data.pool_size)) setMatches(r.data);
      else if (r.data.status === 'running') { setMatchLoading(true); startInternalPoll(id); }
    } catch (e) { if (process.env.NODE_ENV === 'development') console.debug('No prior internal-match results:', e?.message); }
  }, [startKitPoll, startInternalPoll, clearKitPoll, clearInternalPoll]);

  useEffect(() => { if (jobId) { loadKit(jobId); setSearchParams({ job: jobId }); } }, [jobId, loadKit, setSearchParams]);

  const generateKit = async () => {
    if (!jobId) return;
    setGenLoading(true);
    try {
      await sourcingAPI.generateKit(jobId);
      startKitPoll(jobId);
    } catch (e) {
      setGenLoading(false);
      toast.error(e?.response?.data?.detail || 'Failed to start sourcing kit generation');
    }
  };

  const runInternal = async () => {
    if (!jobId) return;
    setMatchLoading(true); setMatches(null);
    try {
      const res = await sourcingAPI.sourceInternal(jobId);
      if (res.data.status === 'ready') {
        setMatches(res.data); setMatchLoading(false);
        if (!res.data.matches?.length) toast.info(`No matches found (searched ${res.data.pool_size} candidates)`);
      } else {
        startInternalPoll(jobId);
      }
    } catch (e) {
      setMatchLoading(false);
      toast.error(e?.response?.data?.detail || 'Failed to run internal match');
    }
  };

  const addToPipeline = async (m) => {
    setAddingId(m.candidate_id);
    try {
      await sourcingAPI.addSourced(jobId, { candidate_id: m.candidate_id, overall_score: m.overall_score });
      toast.success(`${m.name} added to pipeline`);
      setMatches((prev) => ({ ...prev, matches: prev.matches.map((x) => x.candidate_id === m.candidate_id ? { ...x, _added: true } : x) }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to add candidate');
    } finally { setAddingId(null); }
  };

  const persona = kit?.persona || {};
  const bs = kit?.boolean_search || {};
  const outreach = kit?.outreach || {};
  const sal = kit?.salary_band || {};

  return (
    <div className="max-w-5xl mx-auto p-6" data-testid="sourcing-page">
      <div className="mb-6">
        <h1 className="text-3xl font-bold" style={{ color: NAVY, fontFamily: 'Cabinet Grotesk' }}>Candidate Sourcing</h1>
        <p className="text-sm text-slate-500 mt-1">Generate an AI sourcing kit and discover matches from your talent pool — straight from a job description.</p>
      </div>

      <Card className="p-4 mb-6">
        <label className="text-xs font-semibold text-slate-500 block mb-1">Select a job</label>
        <select
          data-testid="sourcing-job-select"
          value={jobId}
          onChange={(e) => setJobId(e.target.value)}
          className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2"
          style={{ '--tw-ring-color': NAVY }}
        >
          <option value="">— Choose a job —</option>
          {jobs.map((j) => <option key={j._id} value={j._id}>{j.title} {j.location ? `· ${j.location}` : ''}</option>)}
        </select>
      </Card>

      {jobId && (
        <Tabs defaultValue="kit" className="w-full">
          <TabsList className="mb-4">
            <TabsTrigger value="kit" data-testid="tab-ai-kit">AI Sourcing Kit</TabsTrigger>
            <TabsTrigger value="internal" data-testid="tab-internal-match">Internal Match</TabsTrigger>
          </TabsList>

          <TabsContent value="kit">
            <Card className="p-6">
              {!kit ? (
                <div className="text-center py-10">
                  <p className="text-sm text-slate-500 mb-4">Generate a Claude-powered sourcing kit: ideal-candidate persona, Boolean/X-ray search strings, target companies, outreach templates and a salary band.</p>
                  <Button data-testid="generate-kit-btn" onClick={generateKit} disabled={genLoading} style={{ background: NAVY }}>
                    {genLoading ? 'Generating with Claude…' : 'Generate Sourcing Kit'}
                  </Button>
                </div>
              ) : (
                <div data-testid="sourcing-kit">
                  <div className="flex justify-end mb-4">
                    <Button data-testid="regenerate-kit-btn" variant="outline" size="sm" onClick={generateKit} disabled={genLoading}>
                      {genLoading ? 'Regenerating…' : 'Regenerate'}
                    </Button>
                  </div>

                  <Section title="Ideal Candidate Persona">
                    <p className="text-sm font-medium text-slate-800 mb-2" data-testid="persona-headline">{persona.headline}</p>
                    <div className="flex flex-wrap gap-2 mb-2 text-xs">
                      {persona.seniority && <Badge variant="secondary">{persona.seniority}</Badge>}
                      {persona.years_experience && <Badge variant="secondary">{persona.years_experience}</Badge>}
                    </div>
                    <div className="flex flex-wrap gap-1.5 mb-1">
                      {(persona.must_have_skills || []).map((s) => <Badge key={`mh-${s}`} style={{ background: NAVY }}>{s}</Badge>)}
                      {(persona.nice_to_have_skills || []).map((s) => <Badge key={`nh-${s}`} variant="outline">{s}</Badge>)}
                    </div>
                    {(persona.typical_backgrounds || []).length > 0 && (
                      <p className="text-xs text-slate-500 mt-2"><span className="font-semibold">Backgrounds:</span> {persona.typical_backgrounds.join(' · ')}</p>
                    )}
                    {(persona.red_flags || []).length > 0 && (
                      <p className="text-xs text-red-500 mt-1"><span className="font-semibold">Red flags:</span> {persona.red_flags.join(' · ')}</p>
                    )}
                  </Section>

                  <Section title="Boolean & X-Ray Search">
                    <BoolRow label="LinkedIn X-Ray (Google)" value={bs.linkedin_xray} testId="bool-linkedin-xray" />
                    <BoolRow label="LinkedIn Recruiter" value={bs.linkedin_recruiter} testId="bool-linkedin-recruiter" />
                    <BoolRow label="GitHub" value={bs.github} testId="bool-github" />
                    <BoolRow label="Google / Resume X-Ray" value={bs.google} testId="bool-google" />
                  </Section>

                  <div className="grid md:grid-cols-2 gap-6">
                    <Section title="Target Companies">
                      <div className="flex flex-wrap gap-1.5" data-testid="target-companies">
                        {(kit.target_companies || []).map((c) => <Badge key={`tc-${c}`} variant="secondary">{c}</Badge>)}
                      </div>
                    </Section>
                    <Section title="Target Titles">
                      <div className="flex flex-wrap gap-1.5" data-testid="target-titles">
                        {(kit.target_titles || []).map((t) => <Badge key={`tt-${t}`} variant="outline">{t}</Badge>)}
                      </div>
                    </Section>
                  </div>

                  <Section title="Outreach Templates" right={<CopyButton text={`${outreach.email_subject}\n\n${outreach.email_body}`} testId="copy-email" />}>
                    <div className="bg-slate-50 border border-slate-200 rounded-md p-4 mb-3">
                      <p className="text-xs font-semibold text-slate-500 mb-1">Email subject</p>
                      <p className="text-sm font-medium text-slate-800 mb-3" data-testid="email-subject">{outreach.email_subject}</p>
                      <p className="text-xs font-semibold text-slate-500 mb-1">Email body</p>
                      <p className="text-sm text-slate-700 whitespace-pre-wrap" data-testid="email-body">{outreach.email_body}</p>
                    </div>
                    {outreach.linkedin_inmail && (
                      <div className="bg-slate-50 border border-slate-200 rounded-md p-4">
                        <div className="flex items-center justify-between mb-1">
                          <p className="text-xs font-semibold text-slate-500">LinkedIn InMail</p>
                          <CopyButton text={outreach.linkedin_inmail} testId="copy-inmail" />
                        </div>
                        <p className="text-sm text-slate-700 whitespace-pre-wrap" data-testid="inmail-body">{outreach.linkedin_inmail}</p>
                      </div>
                    )}
                  </Section>

                  {sal.low != null && (
                    <Section title="Estimated Salary Band">
                      <div className="flex items-baseline gap-3" data-testid="salary-band">
                        <span className="text-2xl font-bold" style={{ color: NAVY }}>
                          {sal.currency} {Number(sal.low).toLocaleString()} – {Number(sal.high).toLocaleString()}
                        </span>
                        {sal.median != null && <span className="text-sm text-slate-500">median {sal.currency} {Number(sal.median).toLocaleString()}</span>}
                      </div>
                      {sal.basis && <p className="text-xs text-slate-500 mt-1">{sal.basis}</p>}
                    </Section>
                  )}
                  <p className="text-[11px] text-slate-400 mt-4">AI-generated estimates. Boolean strings and salary bands are starting points — verify before use.</p>
                </div>
              )}
            </Card>
          </TabsContent>

          <TabsContent value="internal">
            <Card className="p-6">
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm text-slate-500">Rank candidates from your other jobs against this role and add strong matches to the pipeline.</p>
                <Button data-testid="run-internal-btn" onClick={runInternal} disabled={matchLoading} style={{ background: NAVY }}>
                  {matchLoading ? 'Matching…' : 'Find Matches'}
                </Button>
              </div>

              {matches && matches.matches?.length === 0 && (
                <p className="text-sm text-slate-400 text-center py-6" data-testid="no-matches">No matches found in your talent pool ({matches.pool_size} searched).</p>
              )}

              {matches?.matches?.length > 0 && (
                <div className="space-y-3" data-testid="internal-matches">
                  {matches.top_recommendation && (
                    <div className="text-xs bg-blue-50 border border-blue-100 rounded-md p-3 text-slate-700 mb-2">
                      <span className="font-semibold" style={{ color: NAVY }}>Top pick: </span>{matches.top_recommendation}
                    </div>
                  )}
                  {matches.matches.map((m) => (
                    <div key={m.candidate_id} className="border border-slate-200 rounded-lg p-4" data-testid={`match-${m.candidate_id}`}>
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-semibold text-slate-800">{m.name}</span>
                            <Badge style={{ background: (m.overall_score || 0) >= 80 ? '#16A34A' : NAVY }}>{m.overall_score}</Badge>
                            {m.fit_recommendation && <span className="text-xs text-slate-500">{m.fit_recommendation}</span>}
                          </div>
                          {m.email && <p className="text-xs text-slate-400 mb-1">{m.email}</p>}
                          {m.summary && <p className="text-sm text-slate-600 mb-2">{m.summary}</p>}
                          <div className="flex flex-wrap gap-1">
                            {(m.key_strengths || []).map((s) => <span key={`str-${s}`} className="text-[11px] bg-green-50 text-green-700 px-2 py-0.5 rounded">+ {s}</span>)}
                            {(m.key_gaps || []).map((g) => <span key={`gap-${g}`} className="text-[11px] bg-amber-50 text-amber-700 px-2 py-0.5 rounded">− {g}</span>)}
                          </div>
                        </div>
                        <Button
                          data-testid={`add-match-${m.candidate_id}`}
                          size="sm"
                          variant={m._added ? 'outline' : 'default'}
                          disabled={m._added || addingId === m.candidate_id}
                          onClick={() => addToPipeline(m)}
                          style={m._added ? {} : { background: NAVY }}
                        >
                          {m._added ? 'Added ✓' : addingId === m.candidate_id ? 'Adding…' : 'Add to pipeline'}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
